import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Upload, FileText, Send, CheckCircle, XCircle, Loader2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BulkSend = () => {
  const [file, setFile] = useState(null);
  const [subject, setSubject] = useState("Message from Delta AI Academy");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get(`${API}/templates`);
        setTemplates(response.data.templates || []);
      } catch (error) {
        console.error("Failed to fetch templates:", error);
      }
    };
    fetchTemplates();
  }, []);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
        setResults(null);
      } else {
        toast.error("Please upload a CSV file");
      }
    }
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setResults(null);
      } else {
        toast.error("Please upload a CSV file");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a CSV file first");
      return;
    }

    setLoading(true);
    setResults(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const params = new URLSearchParams();
      params.append("subject", subject);
      if (templateId && templateId !== "default") params.append("template_id", templateId);
      
      const response = await axios.post(`${API}/email/bulk?${params.toString()}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      
      console.log("done")
      // setResults(response.data.results);
      toast.success(`Bulk email completed`);
      console.log("done")
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to send bulk emails");
    } finally {
      setLoading(false);
    }
  };

  const downloadSampleCSV = () => {
    const csvContent = "email\nuser1@example.com\nuser2@example.com\nuser3@example.com";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_emails.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="bulk-send-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
          Bulk Email Send
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload a CSV file with email addresses to send bulk emails
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
          <CardHeader className="border-b border-[#1a1a1a] pb-4">
            <CardTitle className="font-heading text-xl font-semibold flex items-center gap-2 text-white">
              <Upload className="w-5 h-5 text-cyan-400" />
              Upload CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-slate-400">Email Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject"
                className="h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white"
                data-testid="input-bulk-subject"
              />
            </div>

            {/* Template */}
            <div className="space-y-2">
              <Label className="text-slate-400">Email Template</Label>
              <Select value={templateId} onValueChange={setTemplateId}>
                <SelectTrigger className="h-10 rounded-lg border-[#222] bg-[#0d0d0d] text-white" data-testid="select-bulk-template">
                  <SelectValue placeholder="Use default template" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#222]">
                  <SelectItem value="default" className="text-slate-300">Default Template</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="text-slate-300">{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dropzone */}
            <div
              className={`dropzone ${dragActive ? 'active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
              data-testid="dropzone"
            >
              <input
                id="file-input"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
                data-testid="input-file"
              />
              <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-cyan-400' : 'text-slate-600'}`} />
              <p className="text-sm text-slate-400 font-medium">
                {file ? file.name : "Drag and drop your CSV file here"}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                or click to browse
              </p>
            </div>

            {/* File info */}
            {file && (
              <div className="flex items-center gap-3 p-3 bg-[#0d0d0d] rounded-lg border border-[#1a1a1a]">
                <FileText className="w-5 h-5 text-cyan-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{file.name}</p>
                  <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    setResults(null);
                  }}
                  className="text-slate-500 hover:text-white"
                  data-testid="btn-remove-file"
                >
                  Remove
                </Button>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleUpload}
                disabled={!file || loading}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition-all active:scale-95 h-10"
                data-testid="btn-send-bulk"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Bulk Emails
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={downloadSampleCSV}
                className="rounded-lg border-[#222] text-slate-400 hover:text-white"
                data-testid="btn-download-sample"
              >
                <Download className="w-4 h-4 mr-2" />
                Sample
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card className="bg-[#111] border-[#1a1a1a] rounded-lg">
          <CardHeader className="border-b border-[#1a1a1a] pb-4">
            <CardTitle className="font-heading text-xl font-semibold text-white">
              Results
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {results ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-[#0d0d0d] rounded-lg border border-[#1a1a1a]">
                    <p className="text-2xl font-heading font-bold text-white">{results.total}</p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Total</p>
                  </div>
                  <div className="text-center p-4 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                    <p className="text-2xl font-heading font-bold text-cyan-400">{results.successful}</p>
                    <p className="text-xs text-cyan-400 uppercase tracking-wider">Sent</p>
                  </div>
                  <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                    <p className="text-2xl font-heading font-bold text-red-400">{results.failed}</p>
                    <p className="text-xs text-red-400 uppercase tracking-wider">Failed</p>
                  </div>
                </div>

                {/* Details */}
                <div className="max-h-64 overflow-y-auto">
                  <div className="space-y-2">
                    {results.details.map((detail, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between p-3 bg-[#0d0d0d] rounded-lg border border-[#1a1a1a]"
                      >
                        <span className="text-sm font-mono text-slate-300 truncate flex-1 mr-3">
                          {detail.email}
                        </span>
                        {detail.status === 'sent' ? (
                          <span className="flex items-center text-xs text-cyan-400">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Sent
                          </span>
                        ) : (
                          <span className="flex items-center text-xs text-red-400">
                            <XCircle className="w-4 h-4 mr-1" />
                            Failed
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto text-slate-700 mb-3" />
                <p className="text-sm">Upload a CSV file to see results</p>
                <p className="text-xs text-slate-600 mt-1">
                  CSV should have an "email" column
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="mt-6 bg-[#111] border-[#1a1a1a] rounded-lg">
        <CardContent className="p-6">
          <h3 className="font-heading text-lg font-semibold text-white mb-3">
            CSV Format Instructions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-2">Your CSV file should have emails (with or without header):</p>
              <pre className="p-3 bg-[#0a0a0a] text-cyan-400 text-sm rounded-lg font-mono overflow-x-auto border border-[#1a1a1a]">
{`email
user1@example.com
user2@example.com
user3@example.com`}
              </pre>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-2">Or just a list of emails:</p>
              <pre className="p-3 bg-[#0a0a0a] text-cyan-400 text-sm rounded-lg font-mono overflow-x-auto border border-[#1a1a1a]">
{`user1@example.com
user2@example.com
user3@example.com`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkSend;
